import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import Navbar from './Navbar';
import Footer from './Footer';
import PlanCard from './AllPlanCard';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Define the Plan interface based on the API response
interface PlanStep {
  id: number;
  order: number;
  place: string;
  place_name: string;
  activity: string;
  start_time: string;
  duration_minutes: number;
  suggestion: string;
}

interface Plan {
  id: number;
  name: string;
  description: string;
  suggested_season: string;
  suggested_months: string;
  created_at: string;
  steps: PlanStep[];
}

// Fetch function for plans
const fetchPlans = async (page: number, pageSize: number): Promise<{ plans: Plan[]; totalPages: number }> => {
  const res = await fetch(`${BASE_URL}/api/plans/`);
  if (!res.ok) throw new Error("خطا در دریافت برنامه‌ها");
  const json = await res.json();
  
  const dataArray = Array.isArray(json) ? json : (json.data || []);
  
  // Client-side pagination
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = dataArray.slice(startIndex, endIndex);

  return {
    plans: paginatedData,
    totalPages: Math.ceil(dataArray.length / pageSize),
  };
};


const AllPlans: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['plans', currentPage],
    queryFn: () => fetchPlans(currentPage, pageSize),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const plans = data?.plans || [];
  const totalPages = data?.totalPages || 1;

  const handlePageChange = (page: number) => {
    window.scrollTo(0, 0);
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const max = 5;
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + max - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="site-container pb-1 pt-20 tablet:py-1">
        <h1 className="text-lg font-myYekanFaNumDemiBold mb-6 mt-12 text-gray-800 border-b pb-4">
          برنامه‌های سفر 
        </h1>
        
        <div className="flex flex-col lg:flex-row gap-6 font-myYekanFaNumRegular">
          {/* ---------------- PLANS LIST ---------------- */}
          <div className="w-full">
            {isLoading ? (
              <div className="text-center py-20 text-gray-500">در حال بارگذاری برنامه‌ها...</div>
            ) : isError ? (
              <div className="text-center py-20 text-red-600">{(error as Error).message}</div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {plans.map(plan => (
                    <PlanCard key={plan.id} plan={plan} />
                  ))}
                </div>

                {plans.length === 0 && (
                  <div className="text-center py-20 text-gray-500 border rounded-lg bg-white">
                    برنامه‌ای یافت نشد
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-10 flex justify-center">
                    <nav className="flex items-center gap-2">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        className={`p-2 rounded-lg ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-600'}`}
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                      {getPageNumbers().map(p => (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                            currentPage === p
                              ? 'bg-[#038545] text-white shadow-md'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        className={`p-2 rounded-lg ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-200 text-gray-600'}`}
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AllPlans;
